// =============================================================================
// MinimapView.ts — Minimapa no canto: o tabuleiro inteiro numa espiada só, com
// a chapa (amigos), os bosses e a janela do que a sua tela alcança.
// Jogadores e bosses chegam SEMPRE no snapshot (não sofrem cull de AOI), então
// o minimapa funciona sem nenhum dado extra do servidor.
// =============================================================================
import { EFLAG, PSTATE, type GameStartPlayer, type Snapshot } from '../../../shared/protocol';
import { CHARACTER_TABLE } from '../../../shared/characters';
import { BOSS_TABLE, ETYPE_IDS } from '../../../shared/enemies';
import { $ } from './dom';

const SIZE = 180;                 // lado do canvas em px CSS (a arena é quadrada)
const PLAYER_DOT = 3.5;           // raio do ponto de cada jogador

const cssColor = (cor: number): string => `#${cor.toString(16).padStart(6, '0')}`;

export class MinimapView {
  private readonly canvas = $('hud-minimap') as HTMLCanvasElement;
  private readonly ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;
  private arena = { w: 4000, h: 4000 };
  private youId = '';
  private playersInfo = new Map<string, GameStartPlayer>();
  private enabled = true;   // Toffoli Apagou (#101): minimapa sob sigilo de 100 anos

  start(arena: { w: number; h: number }, youId: string, playersInfo: Map<string, GameStartPlayer>): void {
    this.arena = arena;
    this.youId = youId;
    this.playersInfo = playersInfo;
    // Resolução interna maior em telas retina pros pontos não virarem borrão
    const dpr = Math.min(devicePixelRatio || 1, 2);
    this.canvas.width = SIZE * dpr;
    this.canvas.height = SIZE * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // Modificador Toffoli Apagou: some com o minimapa a partida inteira
  setEnabled(on: boolean): void {
    this.enabled = on;
    this.canvas.style.display = on ? '' : 'none';
  }

  update(snap: Snapshot): void {
    if (!this.enabled) return;
    const ctx = this.ctx;
    const kx = SIZE / this.arena.w;
    const ky = SIZE / this.arena.h;

    ctx.clearRect(0, 0, SIZE, SIZE);

    // Linhas de referência dividindo o tabuleiro em quadrantes
    ctx.strokeStyle = 'rgba(255,255,255,.08)';
    ctx.lineWidth = 1;
    for (let i = 1; i < 4; i++) {
      const q = (SIZE / 4) * i;
      ctx.beginPath(); ctx.moveTo(q, 0); ctx.lineTo(q, SIZE); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, q); ctx.lineTo(SIZE, q); ctx.stroke();
    }

    // Janela do que cabe na sua tela (câmera sem zoom, centrada em você)
    const me = snap.players.find((p) => p.id === this.youId);
    if (me) {
      const vw = innerWidth * kx;
      const vh = innerHeight * ky;
      ctx.strokeStyle = 'rgba(255,255,255,.35)';
      ctx.strokeRect(me.x * kx - vw / 2, me.y * ky - vh / 2, vw, vh);
    }

    // Bosses: emoji com halo vermelho pulsando (landmark que não pode passar batido)
    const pulse = 0.55 + 0.45 * Math.abs(Math.sin(snap.t * 4));
    for (const e of snap.enemies) {
      if (!(e[5] & EFLAG.BOSS)) continue;
      const boss = BOSS_TABLE[ETYPE_IDS[e[1]]];
      const x = e[2] * kx;
      const y = e[3] * ky;
      ctx.fillStyle = `rgba(212,42,42,${(pulse * 0.5).toFixed(2)})`;
      ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2); ctx.fill();
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(boss?.emoji ?? '💀', x, y);
    }

    // A chapa: cada um com a cor do candidato; você ganha um anel branco.
    // Caído pisca em vermelho (corre lá reviver!); zumbi/morto fica apagado.
    for (const p of snap.players) {
      const x = p.x * kx;
      const y = p.y * ky;
      const cor = CHARACTER_TABLE[this.playersInfo.get(p.id)?.charId ?? '']?.cor ?? 0xffffff;
      const downed = p.st === PSTATE.DOWNED;
      const fora = p.st === PSTATE.ZOMBIE || p.st === PSTATE.DEAD;
      ctx.globalAlpha = fora ? 0.35 : downed ? pulse : 1;
      ctx.fillStyle = fora ? '#888' : downed ? '#d42a2a' : cssColor(cor);
      ctx.beginPath(); ctx.arc(x, y, PLAYER_DOT, 0, Math.PI * 2); ctx.fill();
      if (p.id === this.youId) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(x, y, PLAYER_DOT + 2, 0, Math.PI * 2); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
  }
}
